export interface RenderGeneratedFileHeaderOptions {
  /** Config / authority path shown in the header. Defaults to `athena.config.ts`. */
  source?: string;
}

const DEFAULT_GENERATED_HEADER_SOURCE = "athena.config.ts";

/**
 * Leading Athena generated-file header (legacy + current).
 * Matches one or more consecutive Athena banners so re-runs cannot stack them.
 */
const ATHENA_GENERATED_HEADER_RE =
  /^(?:\uFEFF?\s*\/\*\*[\s\S]*?(?:@generated|Generated by Athena)[\s\S]*?\*\/\s*)+/;

/**
 * Exact signature used by the pre-canonical Athena generator banner.
 * Keep this separate from the tolerant normalizer above: marker prose in a
 * hand-authored file must never grant generator ownership.
 */
const ATHENA_LEGACY_GENERATED_HEADER_RE =
  /^(?:\uFEFF?[ \t]*\/\*\*\r?\n[ \t]*\* Generated by Athena\.\r?\n[ \t]*\*[ \t]*\r?\n[ \t]*\* Do not edit manually\.\r?\n[ \t]*\*[ \t]*\r?\n[ \t]*\* Source:\r?\n[ \t]*\* athena\.config\.ts\r?\n)/;

/**
 * Exact leading prefix shared by every Athena-generated TypeScript artifact.
 * Prefer {@link isAthenaGeneratedSource} over path/filename heuristics.
 */
export const ATHENA_GENERATED_FILE_PREFIX =
  "/**\n * @generated\n * Generated by Athena." as const;

/**
 * Canonical header for every Athena-generated TypeScript artifact.
 * Prefer {@link renderGeneratedFileHeader} over copying this string.
 */
export function renderGeneratedFileHeader(
  options: RenderGeneratedFileHeaderOptions = {}
): string {
  const source = (options.source ?? DEFAULT_GENERATED_HEADER_SOURCE).trim();
  const sourceLine =
    source.length > 0 ? source : DEFAULT_GENERATED_HEADER_SOURCE;
  return `${ATHENA_GENERATED_FILE_PREFIX}
 *
 * DO NOT EDIT MANUALLY.
 * Changes will be overwritten by \`athena-js generate\`.
 *
 * Source: ${sourceLine}
 */
`;
}

/** True when `source` begins with a recognized Athena generated-file header. */
export function isAthenaGeneratedSource(source: string): boolean {
  const normalized = source.replace(/^\uFEFF/, "");
  return (
    normalized.startsWith(ATHENA_GENERATED_FILE_PREFIX) ||
    ATHENA_LEGACY_GENERATED_HEADER_RE.test(normalized)
  );
}

/**
 * Banner required on every generated Athena artifact (Architecture 4.0).
 * @deprecated Prefer {@link renderGeneratedFileHeader}. Kept as the default
 * rendered header for existing imports.
 */
export const GENERATED_FILE_BANNER = renderGeneratedFileHeader();

/** Strip any leading Athena generated header(s) from file content. */
export function stripGeneratedFileHeader(content: string): string {
  return content.replace(ATHENA_GENERATED_HEADER_RE, "");
}

/**
 * Ensure content starts with exactly one canonical Athena generated header.
 * Idempotent across legacy and current wording (never stacks duplicates).
 */
export function withGeneratedFileBanner(
  content: string,
  options: RenderGeneratedFileHeaderOptions = {}
): string {
  const body = stripGeneratedFileHeader(
    content.replace(/^\uFEFF?/, "")
  ).replace(/^\s*\n/, "");
  return `${renderGeneratedFileHeader(options)}\n${body}`;
}
